package com.evoapps.evoshift.domain.model

/** Days of a roster's month (inclusive) that "Email the roster" covers (SH-45). */
data class DayRange(val fromDay: Int, val toDay: Int) {
    init {
        require(fromDay in 1..31 && toDay in fromDay..31) { "Invalid days $fromDay-$toDay" }
    }
}

/**
 * What "Email the roster" queued (SH-45): the mails, and the people skipped for having no e-mail
 * address. SH-242: also who those people are ([withoutEmailNames]) and the queued mails' ids, so
 * the app can ask what became of them ([RosterMailDelivery]).
 */
data class RosterMailResult(
    val queued: Int,
    val withoutEmail: Int,
    val withoutEmailNames: List<String> = emptyList(),
    val mailIds: List<String> = emptyList()
)

/**
 * SH-242: what became of the roster e-mails just sent (functions/mailStatus.js): accepted by the
 * mail service ([sent]), refused ([failed], the people's names in [failedNames]) or still on
 * their way ([pending]).
 */
data class RosterMailDelivery(val sent: Int, val failed: Int, val pending: Int, val failedNames: List<String> = emptyList())

/** Why the roster could not be sent (functions/index.js sendRosterEmail). */
enum class RosterMailRefusal {
    /** It was sent to the same people less than 10 minutes ago. */
    TOO_SOON,
    /** Only a published month can be sent. */
    NOT_PUBLISHED,
    /** The workplace's plan ended (SH-65). */
    PLAN_ENDED,
    /** E-mail is not configured on the server. */
    NOT_SET_UP
}

/**
 * SH-261: the whole roster printed for everyone (functions/rosterPrint.js): a PDF behind a link
 * valid for a day, which the app opens (the system's viewer prints it).
 */
data class PrintedRoster(val url: String, val fileName: String)

class RosterMailRefusedException(val reason: RosterMailRefusal) : Exception("Roster e-mail refused: $reason")

/**
 * The person's private calendar subscription (SH-45), off until they turn it on. [url] is known only
 * right after creating it (the server keeps a hash of the token, not the link). [createdAt] (ISO
 * instant) tells this link apart from an earlier one (SH-202: a link remembered on this device).
 */
data class CalendarFeed(val active: Boolean, val url: String? = null, val createdAt: String? = null)

/** A calendar file of the person's shifts for "Add to calendar". */
data class CalendarFile(val name: String, val text: String, val events: Int)
